const blockPropsEqual = (a, b) => {
  if (a.id !== b.id || a.type !== b.type || a.content !== b.content) return false;
  const aProps = a.props || {};
  const bProps = b.props || {};
  const keys = new Set([...Object.keys(aProps), ...Object.keys(bProps)]);
  for (const key of keys) {
    if (aProps[key] !== bProps[key]) return false;
  }
  return a.children === b.children;
};

/** Skip re-render when a sibling block changed (e.g. a checkbox tick). */
export const blockRendererAreEqual = (prev, next) => {
  if (!blockPropsEqual(prev.block, next.block)) return false;
  if (prev.prevType !== next.prevType || prev.nextType !== next.nextType) return false;
  if (prev.listNumber !== next.listNumber) return false;
  if (prev.activeId !== next.activeId) return false;
  if (prev.fileDragActive !== next.fileDragActive) return false;
  // Variable values changed (host memoizes the resolver on its variables
  // data) — every block re-renders so pills refresh live
  if (prev.resolveVariable !== next.resolveVariable) return false;
  if (prev.hasVariablesBlock !== next.hasVariablesBlock) return false;
  // Variables block edits host-owned doc.variables / campaign context
  if (
    prev.docVariables !== next.docVariables ||
    prev.onDocVariablesChange !== next.onDocVariablesChange ||
    prev.onRenameVariable !== next.onRenameVariable ||
    prev.variablesHistoryEpoch !== next.variablesHistoryEpoch ||
    prev.variableCampaign !== next.variableCampaign ||
    prev.allSprints !== next.allSprints ||
    prev.onCampaignVariablesChange !== next.onCampaignVariablesChange ||
    prev.missionVariables !== next.missionVariables ||
    prev.onMissionVariablesChange !== next.onMissionVariablesChange ||
    prev.onPatchInheritedVariable !== next.onPatchInheritedVariable ||
    prev.variablesColumnWidths !== next.variablesColumnWidths ||
    prev.onVariablesColumnWidthsChange !== next.onVariablesColumnWidthsChange
  ) {
    return false;
  }

  const id = prev.block.id;
  if (prev.selectedBlockId !== next.selectedBlockId) {
    if (prev.selectedBlockId === id || next.selectedBlockId === id) return false;
  }

  const wasMulti = prev.multiSelectedIds?.includes(id) ?? false;
  const nowMulti = next.multiSelectedIds?.includes(id) ?? false;
  if (wasMulti !== nowMulti) return false;
  // While this block stays multi-selected, the id list can still grow/shrink
  // (top-down drag). Handle-menu wrapIds are snapshotted from these props —
  // skipping that update left convert wrapping only the first 1–2 blocks.
  if ((wasMulti || nowMulti) && prev.multiSelectedIds !== next.multiSelectedIds) {
    return false;
  }

  if (prev.dropTarget !== next.dropTarget) {
    if (prev.dropTarget?.targetId === id || next.dropTarget?.targetId === id) return false;
  }

  // Containers must re-render on editor-wide state changes so DESCENDANT
  // blocks (inside callouts/columns/table cells) receive fresh props —
  // otherwise drop indicators, selection and tick overrides never reach
  // nested blocks. Tables store cells in props.tableData, not children.
  if (prev.block.children?.length || prev.block.type === 'table') {
    if (prev.dropTarget !== next.dropTarget) return false;
    if (prev.fileDragActive !== next.fileDragActive) return false;
    if (prev.selectedBlockId !== next.selectedBlockId) return false;
    if (prev.multiSelectedIds !== next.multiSelectedIds) return false;
    if (prev.tickOverrides !== next.tickOverrides) return false;
    if (prev.fileOverrides !== next.fileOverrides) return false;
  }

  if (prev.block.type === 'checkbox') {
    const prevHas =
      prev.tickOverrides && Object.prototype.hasOwnProperty.call(prev.tickOverrides, id);
    const nextHas =
      next.tickOverrides && Object.prototype.hasOwnProperty.call(next.tickOverrides, id);
    if (prevHas !== nextHas) return false;
    if (prevHas && prev.tickOverrides[id] !== next.tickOverrides[id]) return false;
  }

  if (prev.block.type === 'file-upload') {
    if (prev.fileOverrides !== next.fileOverrides) return false;
    if (prev.missionId !== next.missionId || prev.docId !== next.docId) return false;
    const prevFiles = prev.fileOverrides?.[id];
    const nextFiles = next.fileOverrides?.[id];
    if (prevFiles !== nextFiles) return false;
  }

  const prevLinks = prev.snippetBacklinks?.[id];
  const nextLinks = next.snippetBacklinks?.[id];
  if (prevLinks !== nextLinks) return false;
  if (
    prev.snippetBacklinks !== next.snippetBacklinks &&
    (prev.block.children?.length || prev.block.type === 'table')
  ) {
    return false;
  }

  return true;
};
